import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock, FaWhatsapp, FaTelegram } from 'react-icons/fa';
import SectionTitle from '../UI/SectionTitle';
import Form from '../UI/Form';

const Section = styled.section`
  padding: 80px 0;
  background-color: #fff;
  
  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const ContactsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  margin-top: 60px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr;
  }
`;

const ContactInfo = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  padding: 40px;
  height: fit-content;
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const ContactItem = styled.div`
  display: flex;
  align-items: flex-start;
  margin-bottom: 24px;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const ContactIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: #f5f7ff;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #3f51b5;
  margin-right: 16px;
  flex-shrink: 0;
`;

const ContactText = styled.div`
  h4 {
    font-size: 1.1rem;
    color: #333;
    margin: 0 0 8px 0;
  }
  
  p, a {
    margin: 0;
    color: #666;
    text-decoration: none;
    line-height: 1.5;
    display: block;
    
    &:hover {
      color: #3f51b5;
    }
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 16px;
  margin-top: 8px;
`;

const SocialLink = styled.a`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${props => props.color || '#3f51b5'};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s;
  
  &:hover {
    transform: scale(1.1);
  }
`;

const HoursItem = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 8px;
  
  &:last-child {
    margin-bottom: 0;
  }
  
  span:last-child {
    font-weight: 500;
  }
`;

const MapContainer = styled(motion.div)`
  height: 400px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  margin-bottom: 40px;
  
  iframe {
    width: 100%;
    height: 100%;
    border: none;
  }
`;

const FormContainer = styled(motion.div)`
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.06);
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 24px;
  }
`;

const FormTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 24px;
  color: #333;
`;

const ContactsSection = () => {
  const handleFormSubmit = (formData) => {
    // Здесь будет логика отправки данных формы на бэкенд
    console.log('Форма отправлена:', formData);
    alert('Ваше сообщение успешно отправлено! Мы свяжемся с вами в ближайшее время.');
  };

  return (
    <Section id="contacts">
      <Container>
        <SectionTitle
          title="Контакты"
          subtitle="Свяжитесь с нами любым удобным способом"
          centered
        />
        
        <MapContainer
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
        >
          <iframe 
            src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d2245.369520822784!2d37.620795376957806!3d55.75384997994007!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x46b54a50b315e573%3A0xa886bf5a3d9b2e68!2z0JzQvtGB0LrQvtCy0YHQutC40Lkg0JrRgNC10LzQu9GM!5e0!3m2!1sru!2sru!4v1713057296611!5m2!1sru!2sru" 
            allowFullScreen="" 
            loading="lazy" 
            referrerPolicy="no-referrer-when-downgrade"
            title="Карта с нашим местоположением"
          ></iframe>
        </MapContainer>
        
        <ContactsGrid>
          <ContactInfo
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <ContactItem>
              <ContactIcon>
                <FaMapMarkerAlt size={20} />
              </ContactIcon>
              <ContactText>
                <h4>Адрес</h4>
                <p>г. Москва, ул. Тверская, д. 1, офис 123</p>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaPhone size={20} />
              </ContactIcon>
              <ContactText>
                <h4>Телефон</h4>
                <a href="tel:+74951339612">+7 (495) 133-96-12</a>
                <SocialLinks>
                  <SocialLink href="https://wa.me/74951339612" color="#25D366" target="_blank" rel="noopener noreferrer">
                    <FaWhatsapp size={18} />
                  </SocialLink>
                  <SocialLink href="https://t.me/kupitstreych" color="#0088cc" target="_blank" rel="noopener noreferrer">
                    <FaTelegram size={18} />
                  </SocialLink>
                </SocialLinks>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaEnvelope size={20} />
              </ContactIcon>
              <ContactText>
                <h4>Email</h4>
                <a href="mailto:info@купить-стрейч.рф">info@купить-стрейч.рф</a>
              </ContactText>
            </ContactItem>
            
            <ContactItem>
              <ContactIcon>
                <FaClock size={20} />
              </ContactIcon>
              <ContactText>
                <h4>График работы</h4>
                <div style={{ marginTop: '8px' }}>
                  <HoursItem>
                    <span>Понедельник - Пятница:</span>
                    <span>9:00 - 18:00</span>
                  </HoursItem>
                  <HoursItem>
                    <span>Суббота - Воскресенье:</span>
                    <span>Выходной</span>
                  </HoursItem>
                </div>
              </ContactText>
            </ContactItem>
          </ContactInfo>
          
          <FormContainer
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5 }}
            viewport={{ once: true }}
          >
            <FormTitle>Напишите нам</FormTitle>
            <Form 
              fields={[
                { name: 'name', label: 'Ваше имя', type: 'text', required: true },
                { name: 'phone', label: 'Телефон', type: 'tel', required: true },
                { name: 'email', label: 'Email', type: 'email', required: true },
                { name: 'message', label: 'Сообщение', type: 'textarea', required: true }
              ]}
              submitButtonText="Отправить сообщение"
              onSubmit={handleFormSubmit}
            />
          </FormContainer>
        </ContactsGrid>
      </Container>
    </Section>
  );
};

export default ContactsSection; 